import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Clock, User, ArrowLeft, ArrowRight } from 'lucide-react'
import { useApp } from '../context/AppContext'
import { t } from '../i18n'
import { articles } from '../data/articles'
import { useScrollAnimateChildren } from '../hooks/useScrollAnimation'
import { useParallax } from '../hooks/useParallax'

const categories = {
  ar: [
    { key: 'all', label: 'الكل' },
    { key: 'land', label: 'الشحن البري' },
    { key: 'customs', label: 'الجمارك' },
  ],
  en: [
    { key: 'all', label: 'All' },
    { key: 'land', label: 'Land Shipping' },
    { key: 'customs', label: 'Customs' },
  ],
}

export default function Articles() {
  const { lang } = useApp()
  const isAr = lang === 'ar'
  const navigate = useNavigate()
  const [activeCategory, setActiveCategory] = useState('all')
  const ArrowIcon = isAr ? ArrowLeft : ArrowRight

  const filtered =
    activeCategory === 'all' ? articles : articles.filter((a) => a.category === activeCategory)

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString(isAr ? 'ar-EG' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const heroRef = useRef<HTMLElement>(null)
  useEffect(() => {
    const el = heroRef.current
    if (!el) return
    setTimeout(() => el.classList.add('anim-visible'), 80)
  }, [])

  const gridRef = useScrollAnimateChildren()
  const parallaxRef = useParallax()

  useEffect(() => {
    const parent = gridRef.current
    if (!parent) return
    const children = Array.from(parent.children) as HTMLElement[]
    children.forEach((child) => child.classList.remove('anim-visible'))
    const timers = children.map((child, i) =>
      setTimeout(() => child.classList.add('anim-visible'), i * 80)
    )
    return () => timers.forEach(clearTimeout)
  }, [activeCategory])

  return (
    <main className="page articles-page" dir={isAr ? 'rtl' : 'ltr'}>
      {/* Hero */}
      <section
        className="page-hero articles-hero"
        ref={heroRef as React.RefObject<HTMLElement>}
      >
        <div className="page-hero-overlay" />
        <img
          ref={parallaxRef}
          src="/assets/images/splash-truck.jpg"
          alt="articles"
          className="page-hero-img parallax-img"
        />
        <div className="page-hero-content">
          <h1>{t(lang, 'articlesHeroTitle')}</h1>
          <p>{t(lang, 'articlesHeroDesc')}</p>
        </div>
      </section>

      {/* Category Filter */}
      <div className="articles-filter-wrap">
        <div className="articles-filter">
          {categories[lang].map((cat) => (
            <button
              key={cat.key}
              className={`filter-btn ${activeCategory === cat.key ? 'active' : ''}`}
              onClick={() => setActiveCategory(cat.key)}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Articles Grid */}
      <section className="section articles-section">
        <div className="section-inner">
          <div
            className="articles-grid"
            ref={gridRef as React.RefObject<HTMLDivElement>}
          >
            {filtered.map((article) => (
              <article
                key={article.id}
                className="article-card anim-child"
                onClick={() => navigate(`/articles/${article.id}`)}
              >
                <div className="article-img-wrap">
                  <img
                    src={article.image}
                    alt={isAr ? article.titleAr : article.titleEn}
                    className="article-img"
                  />
                  <div className="article-category-badge">
                    {categories[lang].find((c) => c.key === article.category)?.label}
                  </div>
                </div>
                <div className="article-content">
                  <div className="article-meta">
                    <span className="article-meta-item">
                      <User size={13} />
                      {isAr ? article.authorAr : article.authorEn}
                    </span>
                    <span className="article-meta-item">
                      <Clock size={13} />
                      {article.readTime} {t(lang, 'minRead')}
                    </span>
                  </div>
                  <h3 className="article-title">
                    {isAr ? article.titleAr : article.titleEn}
                  </h3>
                  <p className="article-excerpt">
                    {isAr ? article.excerptAr : article.excerptEn}
                  </p>
                  <div className="article-footer">
                    <span className="article-date">{formatDate(article.date)}</span>
                    <button className="article-read-more">
                      {t(lang, 'readMore')} <ArrowIcon size={14} />
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </main>
  )
}
